/**
 * ResetPasswordModal Component
 * Admin modal for resetting Q AI user passwords
 *
 * Endpoint: POST /api/admin/qai/users/{userId}/reset-password (FIX Engine - port 7261)
 */

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    InputAdornment,
    IconButton
} from '@mui/material';
import {
    Lock as LockIcon,
    Close as CloseIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { resetQAIUserPassword } from '../../api/admin_api';

// Embedded theme (matches other modals)
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.7)', quaternary: 'rgba(255,255,255,0.5)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    gradients: { primaryAccent: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
    borderRadius: { sm: '8px', md: '12px' }
};

const ResetPasswordModal = ({ open, user, onClose, onSuccess }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const validateForm = () => {
        if (!newPassword) {
            setError('New password is required');
            return false;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return false;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError(null);

        try {
            const result = await resetQAIUserPassword(user.userId, newPassword);

            if (result.success) {
                // Reset form
                setNewPassword('');
                setConfirmPassword('');
                setShowPassword(false);
                setShowConfirmPassword(false);
                onSuccess?.();
            } else {
                setError(result.error || 'Failed to reset password');
            }
        } catch (err) {
            console.error('Error resetting password:', err);
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setNewPassword('');
            setConfirmPassword('');
            setShowPassword(false);
            setShowConfirmPassword(false);
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    background: theme.colors.bg.primary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: theme.borderRadius.md
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: `1px solid ${theme.colors.border.primary}`,
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LockIcon sx={{ color: theme.colors.status.warning }} />
                    <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                        Reset Password
                    </Typography>
                </Box>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ minWidth: 'auto', color: theme.colors.text.quaternary }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {/* User Info */}
                <Box sx={{
                    p: 2,
                    mb: 3,
                    background: theme.colors.bg.secondary,
                    borderRadius: theme.borderRadius.sm,
                    border: `1px solid ${theme.colors.border.primary}`
                }}>
                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                        Resetting password for:
                    </Typography>
                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                        {user?.email || 'Unknown User'}
                    </Typography>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* New Password Input */}
                <Typography sx={{ color: theme.colors.text.secondary, mb: 1, fontWeight: 500 }}>
                    New Password *
                </Typography>
                <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimum 8 characters"
                    value={newPassword}
                    onChange={(e) => {
                        setNewPassword(e.target.value);
                        setError(null);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon sx={{ color: theme.colors.text.quaternary }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    sx={{ color: theme.colors.text.quaternary }}
                                >
                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    helperText={newPassword && newPassword.length < 8 ? 'Password must be at least 8 characters' : ''}
                    error={newPassword && newPassword.length > 0 && newPassword.length < 8}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            background: theme.colors.bg.secondary,
                            color: theme.colors.text.primary,
                            '& fieldset': { borderColor: theme.colors.border.primary }
                        },
                        '& .MuiFormHelperText-root': {
                            color: theme.colors.status.error
                        }
                    }}
                />

                {/* Confirm Password Input */}
                <Typography sx={{ color: theme.colors.text.secondary, mb: 1, fontWeight: 500 }}>
                    Confirm Password *
                </Typography>
                <TextField
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setError(null);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon sx={{ color: theme.colors.text.quaternary }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    edge="end"
                                    sx={{ color: theme.colors.text.quaternary }}
                                >
                                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    helperText={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
                    error={confirmPassword && newPassword !== confirmPassword}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            background: theme.colors.bg.secondary,
                            color: theme.colors.text.primary,
                            '& fieldset': { borderColor: theme.colors.border.primary }
                        },
                        '& .MuiFormHelperText-root': {
                            color: theme.colors.status.error
                        }
                    }}
                />

                {/* Success Preview */}
                {newPassword && newPassword.length >= 8 && newPassword === confirmPassword && (
                    <Box sx={{
                        p: 2,
                        background: theme.colors.status.success + '10',
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.status.success}30`
                    }}>
                        <Typography sx={{ color: theme.colors.status.success, fontWeight: 600 }}>
                            Ready to Reset
                        </Typography>
                        <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.85rem' }}>
                            Click "Reset Password" to update the user's password.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{
                p: 2,
                borderTop: `1px solid ${theme.colors.border.primary}`,
                gap: 1
            }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ color: theme.colors.text.tertiary }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !newPassword || newPassword.length < 8 || newPassword !== confirmPassword}
                    startIcon={loading ? <CircularProgress size={16} /> : <LockIcon />}
                    sx={{
                        background: theme.colors.status.warning,
                        '&:hover': { background: '#d97706' },
                        '&:disabled': { background: theme.colors.bg.tertiary }
                    }}
                >
                    {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ResetPasswordModal;
